import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { mensajeDeError } from '../../lib/mensajeDeError';
import { pintarDia } from '../../lib/horaEspana';

// LA SEMANA Y EL FIN DEL PLAN, DESDE LA FICHA (César y Francisco, 15-10): «que los
// administradores puedan mover la semana de un cliente a voluntad y poder corregir su fecha
// de fin». Mover la semana corre todo el plan, también el final; el fin se corrige aparte.
// Solo administradores. Qué se puede y a qué semanas lo dice el servidor
// (core/inicio_del_plan): aquí solo se pinta.
const largo = (dia) => pintarDia(dia, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).replace(',', '');

const caja = 'mt-3 space-y-2 bg-[#F6F5F2] border border-[#E5E2DB] rounded-lg p-3';
const campo = 'w-full bg-white border border-[#D6D2C9] rounded-md px-2 py-2 text-sm text-[#1C1B19]';

const Formulario = ({ testid, explicacion, children, listo, guardando, onGuardar, onCancelar, motivo, setMotivo }) => (
    <div className={caja} data-testid={testid}>
        {explicacion && <p className="text-xs text-[#57544E]">{explicacion}</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {children}
            <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={300}
                placeholder="Motivo (queda en el historial)" data-testid={`${testid}-motivo`}
                className="bg-white border-[#D6D2C9] text-[#1C1B19]" />
        </div>
        <div className="flex gap-2">
            <Button onClick={onGuardar} disabled={guardando || !listo || !motivo.trim()} data-testid={`${testid}-guardar`}
                className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white h-9">
                {guardando ? 'Guardando…' : 'Guardar'}
            </Button>
            <Button variant="outline" onClick={onCancelar} className="bg-transparent border-[#D6D2C9] text-[#1C1B19] h-9">
                Cancelar
            </Button>
        </div>
    </div>
);

const FechasDelPlan = ({ api, clientId, onGuardado }) => {
    const [estado, setEstado] = useState(null);
    const [abierto, setAbierto] = useState(null);       // 'semana' | 'fin' | null
    const [lunes, setLunes] = useState('');
    const [hasta, setHasta] = useState('');
    const [motivo, setMotivo] = useState('');
    const [guardando, setGuardando] = useState(false);

    const cargar = useCallback(() => {
        api.get(`/admin/clients/${clientId}/inicio`)
            .then((r) => setEstado(r.data))
            .catch((err) => { console.error('fechas del plan: no se pudo leer', err); setEstado(null); });
    }, [api, clientId]);
    useEffect(() => { cargar(); }, [cargar]);

    if (!estado || (!estado.se_puede && !estado.fin_se_puede)) return null;

    const cerrar = () => { setAbierto(null); setLunes(''); setHasta(''); setMotivo(''); };
    const guardar = async (peticion, exito, fallo) => {
        setGuardando(true);
        try {
            await peticion();
            toast.success(exito);
            cerrar();
            cargar();
            onGuardado && onGuardado();
        } catch (err) {
            toast.error(mensajeDeError(err, fallo));
        } finally {
            setGuardando(false);
        }
    };
    const elegida = estado.opciones.find((o) => o.lunes === lunes);

    return (
        <div className="mt-3" data-testid="fechas-del-plan">
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                {estado.se_puede && (
                    <button onClick={() => setAbierto(abierto === 'semana' ? null : 'semana')} data-testid="mover-semana"
                        className="text-[#B43E06] hover:underline">
                        {estado.semana > 0 ? `Mover la semana (va por la ${estado.semana})` : 'Cambiar cuándo empieza'}
                    </button>
                )}
                {estado.fin_se_puede && (
                    <button onClick={() => setAbierto(abierto === 'fin' ? null : 'fin')} data-testid="cambiar-fin"
                        className="text-[#B43E06] hover:underline">
                        {estado.fin ? `Acceso hasta el ${pintarDia(estado.fin)}: corregir` : 'Poner hasta cuándo tiene acceso'}
                    </button>
                )}
            </div>
            {abierto === 'semana' && (
                <Formulario testid="mover-semana-form" motivo={motivo} setMotivo={setMotivo}
                    listo={!!lunes} guardando={guardando} onCancelar={cerrar}
                    explicacion={`Su plan empieza el ${largo(estado.actual)}.`}
                    onGuardar={() => guardar(
                        () => api.post(`/admin/clients/${clientId}/inicio`, { lunes, motivo }),
                        elegida?.semana > 0 ? `Ahora va por la semana ${elegida.semana}` : `Ahora empieza el ${largo(lunes)}`,
                        'No se pudo mover la semana')}>
                    <select value={lunes} onChange={(e) => setLunes(e.target.value)} data-testid="mover-semana-lunes" className={campo}>
                        <option value="">Elige la semana</option>
                        {estado.opciones.map((o) => <option key={o.lunes} value={o.lunes}>{o.texto}</option>)}
                    </select>
                </Formulario>
            )}
            {abierto === 'fin' && (
                <Formulario testid="cambiar-fin-form" motivo={motivo} setMotivo={setMotivo}
                    listo={!!hasta} guardando={guardando} onCancelar={cerrar}
                    onGuardar={() => guardar(
                        () => api.put(`/admin/clients/${clientId}/fin`, { hasta, motivo }),
                        'Fecha de fin guardada', 'No se pudo guardar la fecha de fin')}>
                    <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} min={estado.actual || undefined}
                        data-testid="cambiar-fin-fecha" className="bg-white border-[#D6D2C9] text-[#1C1B19]" />
                </Formulario>
            )}
        </div>
    );
};

export default FechasDelPlan;
